import { ApiClientError, apiRequest, type ApiRequestOptions } from "@/shared/services/api-client";
import type { components } from "@/types/generated/member-invoices-api";

/**
 * 006-9 member invoice payment — typed wrappers for the 017 `/member/*`
 * operations (UI contract §0/§9/§11).
 *
 * Authority model: the ONLY bearer is the existing platform access token
 * obtained from `AuthProvider.getAccessToken()`; it is passed to the shared
 * `apiRequest` client as the `accessToken` option and nothing else. This
 * module never stores a token, never logs one, and never retries a POST —
 * every non-idempotent call is issued exactly once per invocation and the
 * caller decides what to do with the classified outcome.
 *
 * `obligationVersion` is an opaque decimal string echoed verbatim; it is
 * never parsed or converted client-side. The only client-side use beyond the
 * echo is the strict string-equality check between an attempt and its
 * projection (`obligation-version.ts`, UI contract §5.2a step 2 / §5.4).
 */

type Schemas = components["schemas"];

export type MemberInvoiceProjection = Schemas["MemberInvoiceProjection"];
export type MemberInvoiceProjectionList = Schemas["MemberInvoiceProjectionList"];
export type MemberPaymentAttempt = Schemas["MemberPaymentAttempt"];
export type MemberPaymentAttemptSupportOnly = Schemas["MemberPaymentAttemptSupportOnly"];
export type MemberPaymentAttemptRead = MemberPaymentAttempt | MemberPaymentAttemptSupportOnly;
export type MemberPaymentAttemptPrepareRequest = Schemas["MemberPaymentAttemptPrepareRequest"];
export type MemberPaymentAttemptConfirmRequest = Schemas["MemberPaymentAttemptConfirmRequest"];
export type MemberPaymentAttemptContinueRequest = Schemas["MemberPaymentAttemptContinueRequest"];
export type InvoiceLocatorResolveRequest = Schemas["InvoiceLocatorResolveRequest"];
export type InvoiceLocatorContinueRequest = Schemas["InvoiceLocatorContinueRequest"];
export type InvoiceLocatorResolution = Schemas["InvoiceLocatorResolution"];
export type AttemptStaleError = Schemas["AttemptStaleError"];

/**
 * Transport-level classification of a refused or failed `/member/*` call
 * (UI contract §5.1/§7/§11). Sub-codes of `503` are deliberately NOT
 * distinguished — the SPA never branches copy on them. `attempt_stale` is the
 * only 409 envelope the SPA reads (`retryAvailable`, §5.4); every other 409,
 * including the closed `slot_held` envelope from prepare, carries nothing the
 * SPA acts on beyond "re-read the projection" (§5.1), so it is `unexpected`.
 */
export type MemberInvoicesApiErrorKind =
  | "token_unavailable"
  | "unauthorized"
  | "not_available"
  | "invalid_request"
  | "rate_limited"
  | "attempt_stale"
  | "unavailable"
  | "network"
  | "unexpected";

export class MemberInvoicesApiError extends Error {
  readonly kind: MemberInvoicesApiErrorKind;
  readonly correlationId: string | null;
  readonly attemptStale: AttemptStaleError | undefined;

  constructor(
    kind: MemberInvoicesApiErrorKind,
    options: {
      correlationId?: string | null;
      attemptStale?: AttemptStaleError;
    } = {},
  ) {
    super(`Member invoice request failed: ${kind}`);
    this.name = "MemberInvoicesApiError";
    this.kind = kind;
    this.correlationId = options.correlationId ?? null;
    this.attemptStale = options.attemptStale;
  }
}

export interface MemberInvoicesApiDependencies {
  /** `AuthProvider.getAccessToken()`; rejects when no session token can be obtained. */
  getAccessToken: () => Promise<string>;
}

export interface ResolveInvoiceLocatorOptions {
  /**
   * `true` when the route's §0 classification snapshot is not-signed-in: the
   * resolve request is issued with NO `Authorization` header and the token
   * provider is not consulted. Every other `/member/*` call requires a token.
   */
  anonymous: boolean;
}

export interface MemberInvoicesRequestOptions {
  signal?: AbortSignal;
}

export interface MemberInvoicesApi {
  resolveInvoiceLocator(
    body: InvoiceLocatorResolveRequest,
    options: ResolveInvoiceLocatorOptions,
  ): Promise<InvoiceLocatorResolution>;
  continueInvoiceLocator(body: InvoiceLocatorContinueRequest): Promise<InvoiceLocatorResolution>;
  listOpenInvoices(options?: MemberInvoicesRequestOptions): Promise<MemberInvoiceProjectionList>;
  getInvoicePayment(invoiceId: string): Promise<MemberInvoiceProjection>;
  preparePaymentAttempt(
    invoiceId: string,
    body: MemberPaymentAttemptPrepareRequest,
  ): Promise<MemberPaymentAttempt>;
  getPaymentAttempt(attemptId: string): Promise<MemberPaymentAttemptRead>;
  confirmPaymentAttempt(
    attemptId: string,
    body: MemberPaymentAttemptConfirmRequest,
  ): Promise<MemberPaymentAttempt>;
  continuePaymentAttempt(
    attemptId: string,
    body: MemberPaymentAttemptContinueRequest,
  ): Promise<MemberPaymentAttempt>;
}

// The financial service CORS policy is non-credentialed (allow_credentials=false):
// the bearer header is the only authority, so cookies must never ride along.
const MEMBER_REQUEST_DEFAULTS = { credentials: "omit" } as const satisfies ApiRequestOptions;

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function readErrorBody(response: Response): Promise<Record<string, unknown> | undefined> {
  try {
    const parsed: unknown = await response.json();
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function readCorrelationId(body: Record<string, unknown> | undefined): string | null {
  const value = body?.correlationId;
  return typeof value === "string" ? value : null;
}

function isAttemptStaleError(
  body: Record<string, unknown> | undefined,
): body is AttemptStaleError & Record<string, unknown> {
  return (
    body?.code === "attempt_stale" &&
    typeof body.message === "string" &&
    typeof body.retryAvailable === "boolean"
  );
}

async function classifyApiClientError(error: ApiClientError): Promise<MemberInvoicesApiError> {
  const { status } = error.response;
  const body = await readErrorBody(error.response);
  const correlationId = readCorrelationId(body);

  switch (status) {
    case 400:
      return new MemberInvoicesApiError("invalid_request", { correlationId });
    case 401:
      return new MemberInvoicesApiError("unauthorized", { correlationId });
    case 404:
      return new MemberInvoicesApiError("not_available", { correlationId });
    case 409:
      if (isAttemptStaleError(body)) {
        return new MemberInvoicesApiError("attempt_stale", { correlationId, attemptStale: body });
      }
      // Any other 409 (the closed `slot_held` envelope included) is not
      // actionable here: the caller re-reads the projection, never waits.
      return new MemberInvoicesApiError("unexpected", { correlationId });
    case 429:
      return new MemberInvoicesApiError("rate_limited", { correlationId });
    case 503:
      return new MemberInvoicesApiError("unavailable", { correlationId });
    default:
      return new MemberInvoicesApiError("unexpected", { correlationId });
  }
}

async function toMemberInvoicesApiError(error: unknown): Promise<MemberInvoicesApiError> {
  if (error instanceof MemberInvoicesApiError) {
    return error;
  }
  if (error instanceof ApiClientError) {
    return classifyApiClientError(error);
  }
  return new MemberInvoicesApiError("network");
}

export function createMemberInvoicesApi(deps: MemberInvoicesApiDependencies): MemberInvoicesApi {
  async function acquireToken(): Promise<string> {
    try {
      return await deps.getAccessToken();
    } catch {
      // The rejection reason is discarded on purpose: it may describe the
      // refresh transport, and this layer must never surface or log it.
      throw new MemberInvoicesApiError("token_unavailable");
    }
  }

  async function request<T>(
    path: `/${string}`,
    options: {
      method: "GET" | "POST";
      body?: object;
      accessToken?: string;
      signal?: AbortSignal;
    },
  ): Promise<T> {
    try {
      const { data } = await apiRequest<T>("financial", path, {
        ...MEMBER_REQUEST_DEFAULTS,
        method: options.method,
        accessToken: options.accessToken,
        signal: options.signal,
        // Generated request types are interfaces, which are not assignable to
        // the client's index-signature body type; they are plain JSON objects.
        body: options.body as Record<string, unknown> | undefined,
      });
      return data;
    } catch (error) {
      throw await toMemberInvoicesApiError(error);
    }
  }

  async function authenticatedRequest<T>(
    path: `/${string}`,
    options: { method: "GET" | "POST"; body?: object; signal?: AbortSignal },
  ): Promise<T> {
    const accessToken = await acquireToken();
    return request<T>(path, { ...options, accessToken });
  }

  return {
    async resolveInvoiceLocator(body, options) {
      if (options.anonymous) {
        return request<InvoiceLocatorResolution>("/member/invoice-locators/resolve", {
          method: "POST",
          body,
        });
      }
      return authenticatedRequest<InvoiceLocatorResolution>("/member/invoice-locators/resolve", {
        method: "POST",
        body,
      });
    },
    continueInvoiceLocator(body) {
      return authenticatedRequest<InvoiceLocatorResolution>("/member/invoice-locators/continue", {
        method: "POST",
        body,
      });
    },
    listOpenInvoices(options) {
      return authenticatedRequest<MemberInvoiceProjectionList>("/member/invoices/open", {
        method: "GET",
        signal: options?.signal,
      });
    },
    getInvoicePayment(invoiceId) {
      return authenticatedRequest<MemberInvoiceProjection>(
        `/member/invoices/${encodePathSegment(invoiceId)}/payment`,
        { method: "GET" },
      );
    },
    preparePaymentAttempt(invoiceId, body) {
      return authenticatedRequest<MemberPaymentAttempt>(
        `/member/invoices/${encodePathSegment(invoiceId)}/payment-attempts`,
        { method: "POST", body },
      );
    },
    getPaymentAttempt(attemptId) {
      return authenticatedRequest<MemberPaymentAttemptRead>(
        `/member/payment-attempts/${encodePathSegment(attemptId)}`,
        { method: "GET" },
      );
    },
    confirmPaymentAttempt(attemptId, body) {
      return authenticatedRequest<MemberPaymentAttempt>(
        `/member/payment-attempts/${encodePathSegment(attemptId)}/confirm`,
        { method: "POST", body },
      );
    },
    continuePaymentAttempt(attemptId, body) {
      return authenticatedRequest<MemberPaymentAttempt>(
        `/member/payment-attempts/${encodePathSegment(attemptId)}/continue`,
        { method: "POST", body },
      );
    },
  };
}
